import { Calendar, Check, Clock, UserX } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { downloadBookingIcs, listBookings, markOutcome } from '@/services/schedulingService';

import type { BookingListItem, BookingStatus, Interviewer, OutcomeType } from '@/types/scheduling.types';

interface TodayBookingsProps {
  interviewers: Interviewer[];
}

const STATUS_COLORS: Record<BookingStatus, string> = {
  SCHEDULED: 'bg-green-100 text-green-800',
  RESCHEDULED: 'bg-amber-100 text-amber-800',
  CANCELLED: 'bg-red-100 text-red-800',
  NOSHOW: 'bg-gray-100 text-gray-800',
  ATTENDED: 'bg-blue-100 text-blue-800',
};

function getTodayRange(): { from: string; to: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  const today = `${y}-${m}-${d}`;
  return { from: today, to: today };
}

export function TodayBookings({ interviewers }: TodayBookingsProps) {
  const { t } = useTranslation();
  const [bookings, setBookings] = useState<BookingListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterInterviewerId, setFilterInterviewerId] = useState<string>('');
  const [markingId, setMarkingId] = useState<string | null>(null);
  const [downloadingIcsId, setDownloadingIcsId] = useState<string | null>(null);

  const fetchBookings = useCallback(async () => {
    setIsLoading(true);
    try {
      const { from, to } = getTodayRange();
      const data = await listBookings(from, to);
      setBookings(data);
    } catch {
      toast.error(t('scheduling.toasts.error'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void fetchBookings();
  }, [fetchBookings]);

  const filteredBookings = useMemo(() => {
    if (!filterInterviewerId) return bookings;
    return bookings.filter((b) => b.interviewerUserId === filterInterviewerId);
  }, [bookings, filterInterviewerId]);

  const handleMarkOutcome = useCallback(
    async (bookingId: string, outcome: OutcomeType) => {
      setMarkingId(bookingId);
      try {
        await markOutcome(bookingId, outcome);
        toast.success(t('scheduling.attendance.markedSuccess'));
        await fetchBookings();
      } catch {
        toast.error(t('scheduling.toasts.error'));
      } finally {
        setMarkingId(null);
      }
    },
    [fetchBookings, t],
  );

  const canMark = (status: BookingStatus): boolean => status === 'SCHEDULED' || status === 'RESCHEDULED';

  const handleDownloadIcs = useCallback(
    async (bookingId: string) => {
      setDownloadingIcsId(bookingId);
      try {
        const blob = await downloadBookingIcs(bookingId);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'interview.ics';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch {
        toast.error(t('scheduling.toasts.error'));
      } finally {
        setDownloadingIcsId(null);
      }
    },
    [t],
  );

  if (isLoading) {
    return (
      <div className='flex h-64 items-center justify-center'>
        <div className='h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent' />
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      {/* Filter */}
      <div className='flex items-center gap-3'>
        <select
          value={filterInterviewerId}
          onChange={(e) => setFilterInterviewerId(e.target.value)}
          className='rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'>
          <option value=''>{t('scheduling.attendance.filterInterviewer')}</option>
          {interviewers.map((i) => (
            <option key={i.userId} value={i.userId}>
              {i.name}
            </option>
          ))}
        </select>
      </div>

      {/* List */}
      {filteredBookings.length === 0 ? (
        <div className='flex h-48 flex-col items-center justify-center rounded-lg border border-dashed border-gray-300'>
          <Clock className='h-8 w-8 text-gray-300' />
          <p className='mt-2 text-sm text-gray-500'>{t('scheduling.attendance.empty')}</p>
        </div>
      ) : (
        <div className='overflow-hidden rounded-lg border border-gray-200'>
          <table className='w-full'>
            <thead>
              <tr className='border-b bg-gray-50 text-left text-xs font-medium uppercase tracking-wide text-gray-500'>
                <th className='px-4 py-3'>{t('scheduling.attendance.time')}</th>
                <th className='px-4 py-3'>{t('scheduling.attendance.applicant')}</th>
                <th className='px-4 py-3'>{t('scheduling.attendance.interviewer')}</th>
                <th className='px-4 py-3'>{t('scheduling.attendance.status')}</th>
                <th className='px-4 py-3'>{t('scheduling.attendance.actions')}</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-100'>
              {filteredBookings.map((booking) => (
                <tr key={booking.bookingId} className='hover:bg-gray-50'>
                  <td className='px-4 py-3 text-sm text-gray-900'>
                    {new Date(booking.startAtUtc).toLocaleTimeString([], {
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                    {' – '}
                    {new Date(booking.endAtUtc).toLocaleTimeString([], {
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </td>
                  <td className='px-4 py-3 text-sm font-medium text-gray-900'>{booking.applicantName}</td>
                  <td className='px-4 py-3 text-sm text-gray-600'>{booking.interviewerName}</td>
                  <td className='px-4 py-3'>
                    <span
                      className={cn(
                        'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                        STATUS_COLORS[booking.status],
                      )}>
                      {t(`scheduling.bookingStatuses.${booking.status}`)}
                    </span>
                  </td>
                  <td className='px-4 py-3'>
                    <div className='flex flex-wrap gap-1.5'>
                      {canMark(booking.status) ? (
                        <>
                          <button
                            type='button'
                            disabled={markingId === booking.bookingId}
                            onClick={() => void handleMarkOutcome(booking.bookingId, 'ATTENDED')}
                            className='inline-flex items-center gap-1 rounded-md bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50'>
                            <Check className='h-3 w-3' />
                            {t('scheduling.attendance.markAttended')}
                          </button>
                          <button
                            type='button'
                            disabled={markingId === booking.bookingId}
                            onClick={() => void handleMarkOutcome(booking.bookingId, 'NOSHOW')}
                            className='inline-flex items-center gap-1 rounded-md bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50'>
                            <UserX className='h-3 w-3' />
                            {t('scheduling.attendance.markNoShow')}
                          </button>
                        </>
                      ) : (
                        <span className='text-xs text-gray-400'>{t('scheduling.attendance.outcomeRecorded')}</span>
                      )}
                      <button
                        type='button'
                        disabled={downloadingIcsId === booking.bookingId}
                        onClick={() => void handleDownloadIcs(booking.bookingId)}
                        className='inline-flex items-center gap-1 rounded-md bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50'>
                        <Calendar className='h-3 w-3' />
                        {downloadingIcsId === booking.bookingId
                          ? t('scheduling.attendance.downloadingIcs')
                          : t('scheduling.attendance.downloadIcs')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
